import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageMeta from '../components/PageMeta';
import FadeIn from '../components/FadeIn';

const eventTypes = [
  'Wedding Photography',
  'Wedding Videography',
  'Engagement Session',
  'Portrait Session',
  'Indian Wedding',
  'Corporate Photography',
  'Other',
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Please enter a valid email address.';
    if (!form.message.trim()) e.message = 'Please include a message.';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitted(true);
  };

  const inputBase =
    'w-full border-b border-gray-200 bg-transparent py-3 text-sm font-inter font-light text-charcoal placeholder-gray-300 focus:outline-none focus:border-bronze transition-colors duration-200';

  const labelBase =
    'block text-[10px] tracking-[0.3em] uppercase font-inter text-gray-400 mb-2';

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PageMeta
        title="Contact | Dhrumil Desai Photography Christchurch"
        description="Book a wedding photographer or portrait session in Christchurch, New Zealand. Contact Dhrumil Desai Photography to check availability and discuss your event."
      />

      {/* ── Page Header ── */}
      <section className="pt-40 pb-20 px-6 md:px-12 bg-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <p
            className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-5 font-inter"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Get in Touch
          </p>
          <h1
            className="font-playfair text-4xl md:text-6xl text-charcoal leading-tight max-w-xl mx-auto"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Let's start a conversation
          </h1>
          <p
            className="font-inter font-light text-sm md:text-base text-gray-500 mt-6 max-w-sm mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Fill in the form below and Dhrumil will be in touch within 24–48 hours
            to discuss your event and check availability.
          </p>
        </motion.div>
      </section>

      {/* ── Divider ── */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <hr className="border-t border-gray-100" />
      </div>

      {/* ── Form + Info ── */}
      <section className="py-20 px-6 md:px-12 bg-white" aria-labelledby="contact-form-heading">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">

          {/* Form */}
          <div className="lg:col-span-7">
            <FadeIn>
              <h2
                id="contact-form-heading"
                className="font-playfair text-2xl text-charcoal mb-10"
                style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
              >
                Send an Enquiry
              </h2>
            </FadeIn>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="py-16 text-center"
              >
                <div className="w-px h-12 bg-bronze mx-auto mb-8" />
                <h3
                  className="font-playfair text-2xl md:text-3xl text-charcoal mb-4"
                  style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                >
                  Thank you, {form.name.split(' ')[0]}.
                </h3>
                <p
                  className="font-inter font-light text-sm text-gray-500 leading-relaxed max-w-sm mx-auto"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Your message has been received. Dhrumil will be in touch within 24–48 hours to discuss your enquiry.
                </p>
              </motion.div>
            ) : (
              <FadeIn delay={0.1}>
                <form onSubmit={handleSubmit} noValidate aria-label="Contact enquiry form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-8">

                    {/* Name */}
                    <div className="sm:col-span-2 md:col-span-1">
                      <label
                        htmlFor="name"
                        className={labelBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Full Name *
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className={inputBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                        aria-required="true"
                        aria-describedby={errors.name ? 'name-error' : undefined}
                      />
                      {errors.name && (
                        <p
                          id="name-error"
                          className="text-[11px] text-red-400 mt-2 font-inter"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                          role="alert"
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className={labelBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Email Address *
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@email.com"
                        className={inputBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                        aria-required="true"
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      {errors.email && (
                        <p
                          id="email-error"
                          className="text-[11px] text-red-400 mt-2 font-inter"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                          role="alert"
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="phone"
                        className={labelBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Phone Number
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+64 21 000 0000"
                        className={inputBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      />
                    </div>

                    {/* Event Type */}
                    <div>
                      <label
                        htmlFor="eventType"
                        className={labelBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Event Type
                      </label>
                      <select
                        id="eventType"
                        name="eventType"
                        value={form.eventType}
                        onChange={handleChange}
                        className={`${inputBase} cursor-pointer`}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        <option value="" disabled>Select a service</option>
                        {eventTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    {/* Event Date */}
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="eventDate"
                        className={labelBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Event Date
                      </label>
                      <input
                        id="eventDate"
                        type="date"
                        name="eventDate"
                        value={form.eventDate}
                        onChange={handleChange}
                        className={inputBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      />
                    </div>

                    {/* Message */}
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="message"
                        className={labelBase}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Tell Dhrumil a little about your event, your vision, and anything you'd like him to know."
                        className={`${inputBase} resize-none`}
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                        aria-required="true"
                        aria-describedby={errors.message ? 'message-error' : undefined}
                      />
                      {errors.message && (
                        <p
                          id="message-error"
                          className="text-[11px] text-red-400 mt-2 font-inter"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                          role="alert"
                        >
                          {errors.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="border border-charcoal px-12 py-4 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    Send Enquiry
                  </button>
                </form>
              </FadeIn>
            )}
          </div>

          {/* Info sidebar */}
          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="lg:pt-14 space-y-12">

              <div>
                <p
                  className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-4 font-inter"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Location
                </p>
                <p
                  className="font-inter font-light text-sm text-charcoal leading-relaxed"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Based in Christchurch, Canterbury<br />
                  New Zealand<br />
                  <span className="text-gray-400">Available for travel NZ wide</span>
                </p>
              </div>

              <div>
                <p
                  className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-4 font-inter"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Email
                </p>
                <a
                  href="mailto:hello@dhrumildesai.co.nz"
                  className="font-inter font-light text-sm text-charcoal hover:text-bronze transition-colors duration-200"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  hello@dhrumildesai.co.nz
                </a>
              </div>

              <div>
                <p
                  className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-4 font-inter"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Response Time
                </p>
                <p
                  className="font-inter font-light text-sm text-charcoal"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  All enquiries are responded to within 24–48 hours.
                </p>
              </div>

              <div>
                <p
                  className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-4 font-inter"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Social
                </p>
                <div className="flex gap-6">
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs tracking-[0.15em] uppercase font-inter text-charcoal hover:text-bronze transition-colors duration-200 border-b border-gray-200 pb-0.5"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    Instagram
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs tracking-[0.15em] uppercase font-inter text-charcoal hover:text-bronze transition-colors duration-200 border-b border-gray-200 pb-0.5"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    Facebook
                  </a>
                </div>
              </div>

            </div>
          </FadeIn>
        </div>
      </section>
    </motion.main>
  );
}
